import {inject, Injectable, signal} from '@angular/core';
import {
    ChipType,
    ContainersListItem,
    SystemContainerViewWithId,
    ZoneState,
    ZonesListItem,
    ZoneViewData
} from "../../models";
import {DatabaseService} from "../database.service";
import {ParametersService} from "../parameters.service";
import {ZonesSystemService} from "../zones-system.service";
import {HttpClient} from "@angular/common/http";
import {UserService} from "../user.service";
import {catchError, filter, finalize, map, Observable, skip, Subscription, take, tap, throwError} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class ZonesSettingsService {
    private database = inject(DatabaseService);
    private http = inject(HttpClient);
    private parameters = inject(ParametersService);
    private user = inject(UserService);
    private zonesSystem = inject(ZonesSystemService);

    isRequesting = signal(false);
    zonesList = signal<ZonesListItem[]>([]);
    containersList = signal<ContainersListItem[]>([]);

    private zoneWatchers = new Map<number, Subscription>();

    getZonesList(): Observable<void> {
        this.isRequesting.set(true);
        return this.database.getModuleData().pipe(
            finalize(() => this.isRequesting.set(false)),
            tap(() => {
                if (this.parameters.isZonesSystemControllerType()) {
                    this.zonesList.set([]);
                    this.zonesSystem.setContainers();
                    const containers = this.zonesSystem.getContainers();
                    const containersList: ContainersListItem[] = containers.map((container: SystemContainerViewWithId) => ({
                        containerId: container.containerId,
                        name: container.data.containerName,
                        signal: container.data.parameters.signal,
                        zonesList: this.setZonesList(this.filterVisibleZones(container.data.zones)),
                    }));
                    this.containersList.set(containersList);
                    return;
                }

                this.containersList.set([]);
                const rawElements = this.database.zones.getValue()?.['elements'];
                const elements: Array<ZoneViewData | null> = Array.isArray(rawElements) ? rawElements : [];
                this.zonesList.set(this.setZonesList(this.filterVisibleZones(elements)));
            }),
            map(() => undefined),
            catchError((error) => {
                this.zonesList.set([]);
                this.containersList.set([]);
                return throwError(() => error);
            }),
        );
    }

    changeZoneName(zoneListItem: ZonesListItem, name: string): Observable<void> {
        return this.updateZoneDescription(
            zoneListItem,
            {name, iconId: zoneListItem.iconId}
        );
    }

    changeIcon(zoneListItem: ZonesListItem, iconId: number): Observable<void> {
        return this.updateZoneDescription(
            zoneListItem,
            {name: zoneListItem.name, iconId}
        );
    }

    turnOnOffZone(
        zoneListItem: ZonesListItem,
        newZoneStatus: 'zoneOff' | 'zoneOn'
    ): Observable<void> {
        this.updateZone(zoneListItem, {duringChange: true});
        const data = {zone: {id: zoneListItem.zoneId, zoneState: newZoneStatus}};
        return this.http.post(`/api/v1/users/${this.user.user_id}/modules/${this.parameters.selectedModuleHash}/zones`, data).pipe(
            tap(() => {
                this.watchZoneStateChange(zoneListItem);
            }),
            map(() => undefined),
            catchError((error) => {
                this.updateZone(zoneListItem, {duringChange: false});
                return throwError(() => error);
            }),
        );
    }

    resetZone(zoneListItem: ZonesListItem): Observable<void> {
        return this.turnOnOffZone(zoneListItem, 'zoneOn');
    }

    private updateZone(
        zone: ZonesListItem,
        changes: Partial<ZonesListItem>
    ): void {
        const updatedZone = {...zone, ...changes};
        this.zonesList.update((zones) => this.replaceZone(zones, updatedZone));
        this.containersList.update((containers) => containers.map((container) => ({
            ...container,
            zonesList: this.replaceZone(container.zonesList, updatedZone),
        })));
    }

    private watchZoneStateChange(zoneListItem: ZonesListItem): void {
        const initialState = zoneListItem.status;
        const subscription = this.database.zones.pipe(
            filter((zonesData): zonesData is { elements: ZoneViewData[] } => this.database.isZonesDataType(zonesData)),
            map((zonesData) => this.findZone(zonesData, zoneListItem.zoneId)),
            filter((zone): zone is ZoneViewData => zone !== null && zone.zone.zoneState !== initialState),
            take(1),
        ).subscribe((zone) => {
            this.applyZoneFromDatabase(zoneListItem, zone);
        });
        this.registerZoneWatcher(zoneListItem.zoneId, subscription);
    }

    private updateZoneDescription(
        zoneListItem: ZonesListItem,
        dataInput: { name: string; iconId: number },
    ): Observable<void> {
        this.updateZone(zoneListItem, {duringChange: true});
        const data = {
            zones_id: zoneListItem.zoneId,
            description_id: zoneListItem.descriptionId,
            name: dataInput.name,
            icons_id: dataInput.iconId,
        };
        return this.http.put(
            `/api/v1/users/${this.user.user_id}/modules/${this.parameters.selectedModuleHash}/zones/${zoneListItem.zoneId}`,
            data
        ).pipe(
            tap(() => {
                this.watchZoneDescriptionChange(zoneListItem);
            }),
            map(() => undefined),
            catchError((error) => {
                this.updateZone(zoneListItem, {duringChange: false});
                return throwError(() => error);
            }),
        );
    }

    private watchZoneDescriptionChange(zoneListItem: ZonesListItem): void {
        const initialName = zoneListItem.name;
        const initialIconId = zoneListItem.iconId;
        const subscription = this.database.zones.pipe(
            filter((zonesData): zonesData is { elements: ZoneViewData[] } => this.database.isZonesDataType(zonesData)),
            map((zonesData) => this.findZone(zonesData, zoneListItem.zoneId)),
            filter((zone): zone is ZoneViewData => zone !== null),
            skip(1),
            filter((zone) => zone.description.name !== initialName || zone.description.styleId !== initialIconId),
            take(1),
        ).subscribe((zone) => {
            this.applyZoneFromDatabase(zoneListItem, zone);
        });
        this.registerZoneWatcher(zoneListItem.zoneId, subscription);
    }

    // Keeps at most one active watcher subscription per zone and removes it once it completes.
    private registerZoneWatcher(zoneId: number, subscription: Subscription): void {
        this.zoneWatchers.get(zoneId)?.unsubscribe();
        if (subscription.closed) {
            this.zoneWatchers.delete(zoneId);
            return;
        }
        this.zoneWatchers.set(zoneId, subscription);
        subscription.add(() => {
            if (this.zoneWatchers.get(zoneId) === subscription) {
                this.zoneWatchers.delete(zoneId);
            }
        });
    }

    private findZone(zonesData: { elements: ZoneViewData[] }, zoneId: number): ZoneViewData | null {
        if (this.parameters.isZonesSystemControllerType()) {
            const containers = this.zonesSystem.getContainers() as SystemContainerViewWithId[];
            for (const container of containers) {
                const zone = container?.data?.zones?.find((zone) => zone !== null && zone.zone.id === zoneId);
                if (zone) {
                    return zone;
                }
            }
        }
        return zonesData.elements.find((zone) => zone !== null && zone.zone.id === zoneId) ?? null;
    }

    private applyZoneFromDatabase(zoneListItem: ZonesListItem, zone: ZoneViewData): void {
        this.updateZone(zoneListItem, {
            descriptionId: zone.description.id,
            index: zone.zone.index,
            name: zone.description.name,
            icon: zone.description.styleIcon,
            iconId: zone.description.styleId,
            status: zone.zone.zoneState,
            statusBg: this.getChipType(zone.zone.zoneState),
            duringChange: false,
        });
    }

    private replaceZone(zones: ZonesListItem[], updatedZone: ZonesListItem): ZonesListItem[] {
        return zones.map((zone) => zone.zoneId === updatedZone.zoneId ? updatedZone : zone);
    }

    private setZonesList(zones: ZoneViewData[]): ZonesListItem[] {
        return zones.map((zone) => {
            const zonesListItem: ZonesListItem = {
                zoneId: zone.zone.id,
                descriptionId: zone.description.id,
                index: zone.zone.index,
                name: zone.description.name,
                icon: zone.description.styleIcon,
                iconId: zone.description.styleId,
                duringChange: Boolean(zone.zone.duringChange || zone.description.duringChange),
                status: zone.zone.zoneState,
                statusText: '',
                statusBg: this.getChipType(zone.zone.zoneState),
            };
            return zonesListItem;
        })
    }

    private filterVisibleZones(zones: Array<ZoneViewData | null>): ZoneViewData[] {
        return zones.filter((zone): zone is ZoneViewData => zone !== null && zone.zone.visibility);
    }

    private getChipType(status: ZoneState): ChipType {
        switch (status) {
            case 'zoneOff':
                return 'inactive';
            case 'zoneUnregistered':
                return 'inactive';
            case 'sensorDamaged':
                return 'error';
            case 'noCommunication':
                return 'error';
            case 'noAlarm':
                return 'active';
        }
    }
}
